import React, { useEffect, useState } from 'react';
import { getEmployees, getDepartments } from '../services/employeeService';
import { createDailyReport, previewCost } from '../services/dailyReportService';
import { getCategories } from '../services/categoryService';
import { getProjectStages } from '../services/projectService';
import { checkChecklistItem } from '../services/qualityService';
import { isFieldAllowed } from '../constants/fieldPermissions';
import useProjectStore from '../store/projectStore';
import useAuthStore from '../store/authStore';
import useUIStore from '../store/uiStore';

const todayStr = () => new Date().toISOString().slice(0, 10);

const DailyEntryPage = () => {
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);

  const [allEmployees, setAllEmployees] = useState([]);
  const [crew, setCrew] = useState([]); // [{ id, name, role, absent }]
  const [employeeToAdd, setEmployeeToAdd] = useState('');
  const [projectId, setProjectId] = useState('');
  const [entryDate, setEntryDate] = useState(todayStr());
  const [hours, setHours] = useState(8);
  const [workDescription, setWorkDescription] = useState('');
  const [costPreview, setCostPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [checkpointIdx, setCheckpointIdx] = useState(0);
  const [quantity, setQuantity] = useState('');
  const [isRework, setIsRework] = useState(false);
  const [fieldPermissions, setFieldPermissions] = useState(null);

  // Stages on the selected project that are in progress, need inspection,
  // and still have unchecked items - shown as a collapsed nudge so
  // production doesn't forget to check them while logging today's work.
  const [checklistStages, setChecklistStages] = useState([]);
  const [expandedStageId, setExpandedStageId] = useState(null);

  useEffect(() => {
    fetchProjects();
    getEmployees().then((res) => setAllEmployees(res.data.employees || [])).catch(() => {});
    getCategories().then((res) => setCategories(res.data.categories || [])).catch(() => {});
    if (user?.department_id) {
      getDepartments()
        .then((res) => {
          const dept = (res.data.departments || []).find((d) => d.id === user.department_id);
          setFieldPermissions(dept?.field_permissions || null);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showField = (key) => isFieldAllowed(fieldPermissions, 'daily_entry', key);

  const loadChecklistStages = (pid) => {
    if (!pid) {
      setChecklistStages([]);
      return;
    }
    getProjectStages(pid)
      .then((res) => {
        // Not gated on the stage having been formally "Started" - checking
        // off items shouldn't depend on someone remembering that separate
        // click first. Only "done" stages are excluded.
        const pending = (res.data.stages || []).filter((s) => {
          const summary = s.checklist_summary;
          return (
            s.status !== 'done' &&
            s.requires_inspection &&
            summary &&
            (summary.production_checked_count ?? 0) < summary.total_count
          );
        });
        setChecklistStages(pending);
      })
      .catch(() => setChecklistStages([]));
  };

  useEffect(() => {
    loadChecklistStages(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleCheckProductionItem = async (stage, item) => {
    try {
      await checkChecklistItem(stage.checklist_summary.checklist_id, item.id, !item.production_checked, user?.id, 'production');
      loadChecklistStages(projectId);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update item', 'error');
    }
  };

  const selectedCategory = categories.find((c) => String(c.id) === String(categoryId));
  const selectedTask = selectedCategory?.tasks.find((t) => String(t.id) === String(taskId));

  useEffect(() => {
    if (!categoryId && categories.length > 0) {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);

  useEffect(() => {
    setTaskId(selectedCategory?.tasks[0]?.id || '');
    setCheckpointIdx(0);
    setQuantity('');
    setIsRework(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    if (crew.length === 0) {
      setCostPreview(null);
      return;
    }
    const absentIds = crew.filter((m) => m.absent).map((m) => m.id);
    previewCost(crew.map((m) => m.id), hours, absentIds)
      .then((res) => setCostPreview(res.data))
      .catch(() => setCostPreview(null));
  }, [crew, hours]);

  const availableToAdd = allEmployees.filter((e) => !crew.some((m) => m.id === e.id));

  const addEmployee = () => {
    if (!employeeToAdd) return;
    const emp = allEmployees.find((e) => String(e.id) === String(employeeToAdd));
    if (!emp) return;
    setCrew((prev) => [...prev, { id: emp.id, name: emp.name, role: emp.role, absent: false }]);
    setEmployeeToAdd('');
  };

  const removeEmployee = (id) => setCrew((prev) => prev.filter((m) => m.id !== id));

  const toggleAbsent = (id) =>
    setCrew((prev) => prev.map((m) => (m.id === id ? { ...m, absent: !m.absent } : m)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectId || crew.length === 0) {
      showToast('Please select a project and add at least one employee', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const absentIds = crew.filter((m) => m.absent).map((m) => m.id);
      const res = await createDailyReport({
        date: entryDate,
        project_id: Number(projectId),
        supervisor_id: user?.id,
        employee_ids: crew.map((m) => m.id),
        hours_logged: Number(hours),
        absent_member_ids: absentIds,
        work_description: workDescription,
        activity_id: taskId ? Number(taskId) : null,
        checkpoint_label:
          selectedTask?.task_type === 'checkpoint' && !isRework
            ? selectedTask.checkpoints[checkpointIdx]?.label
            : null,
        checkpoint_percent:
          selectedTask?.task_type === 'checkpoint' && !isRework
            ? selectedTask.checkpoints[checkpointIdx]?.percent
            : null,
        quantity: selectedTask?.task_type === 'count' && !isRework ? Number(quantity) || 0 : null,
        is_rework: isRework,
      });
      showToast(
        `Entry saved for ${entryDate} — cost ₹${res.data.total_salary_cost.toLocaleString()} for ${hours} hrs`,
        'success'
      );
      setWorkDescription('');
      setCrew([]);
      setQuantity('');
      setIsRework(false);
      setCheckpointIdx(0);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save daily entry', 'error');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto glass-card p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Daily Entry</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
              <select
                required
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                required
                value={entryDate}
                max={todayStr()}
                onChange={(e) => setEntryDate(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Catching up on missed days? Submit one entry per day - pick that day's date here,
                don't add its hours onto another day's entry.
              </p>
            </div>
          </div>

          {checklistStages.map((stage) => {
            const summary = stage.checklist_summary;
            const remaining = summary.total_count - (summary.production_checked_count ?? 0);
            const expanded = expandedStageId === stage.id;
            return (
              <div key={stage.id}>
                <button
                  type="button"
                  onClick={() => setExpandedStageId(expanded ? null : stage.id)}
                  className="w-full flex items-center justify-between bg-amber-50 hover:bg-amber-100 rounded px-3 py-2.5 text-sm text-left"
                >
                  <span className="text-amber-800 font-medium">
                    {summary.checklist_name} — {remaining} {remaining === 1 ? 'item' : 'items'} to check
                  </span>
                  <span className="text-amber-600">{expanded ? '▲' : '▼'}</span>
                </button>
                {expanded && (
                  <div className="border border-gray-200 rounded mt-1 divide-y divide-gray-100">
                    {(summary.items || []).map((item) => (
                      <label key={item.id} className="flex items-center justify-between px-3 py-2 text-xs cursor-pointer">
                        <span>
                          {item.description}
                          {item.spec ? ` — ${item.spec}` : ''}
                          {item.is_critical && <span className="ml-1 text-red-600 font-semibold">(critical)</span>}
                        </span>
                        <input
                          type="checkbox"
                          checked={!!item.production_checked}
                          onChange={() => handleCheckProductionItem(stage, item)}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {showField('activity') && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
                <select
                  value={taskId}
                  onChange={(e) => { setTaskId(e.target.value); setCheckpointIdx(0); }}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {(selectedCategory?.tasks || []).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {showField('is_rework') && (
            <label className="flex items-center gap-2 text-sm text-amber-700 font-medium">
              <input type="checkbox" checked={isRework} onChange={(e) => setIsRework(e.target.checked)} />
              Rework (hours only, no output credit)
            </label>
          )}

          {showField('activity') && !isRework && selectedTask?.task_type === 'checkpoint' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Checkpoint Reached</label>
              <select
                value={checkpointIdx}
                onChange={(e) => setCheckpointIdx(Number(e.target.value))}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                {(selectedTask.checkpoints || []).map((cp, i) => (
                  <option key={i} value={i}>{cp.label} ({cp.percent}%)</option>
                ))}
              </select>
            </div>
          )}

          {!isRework && selectedTask?.task_type === 'count' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity {selectedTask.unit ? `(${selectedTask.unit})` : ''}
              </label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          )}

          {showField('hours_logged') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours Logged *</label>
              <input
                type="number"
                step="0.5"
                min="0"
                required
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Crew *</label>
            <div className="flex gap-2 mb-2">
              <select
                value={employeeToAdd}
                onChange={(e) => setEmployeeToAdd(e.target.value)}
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Select an employee to add...</option>
                {availableToAdd.map((e) => (
                  <option key={e.id} value={e.id}>{e.name} — {e.role}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={addEmployee}
                className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                + Add
              </button>
            </div>

            {crew.length === 0 ? (
              <p className="text-xs text-gray-400">No one added yet — add each person who worked today.</p>
            ) : (
              <div className="border border-gray-200 rounded divide-y divide-gray-100">
                {crew.map((m) => (
                  <div key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span>{m.name} <span className="text-gray-400">({m.role})</span></span>
                    <span className="flex items-center gap-3">
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={m.absent} onChange={() => toggleAbsent(m.id)} />
                        Absent
                      </label>
                      <button
                        type="button"
                        onClick={() => removeEmployee(m.id)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {showField('work_description') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Description</label>
              <textarea
                rows={3}
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="What was done today..."
              />
            </div>
          )}

          {costPreview && (
            <div className="bg-gray-50 rounded p-4 text-sm space-y-1">
              <div className="flex justify-between font-semibold text-base">
                <span className="text-gray-700">Cost for {hours} hrs</span>
                <span>₹{costPreview.total_salary_cost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-400 text-xs pt-1">
                <span>Crew's full-day (8 hr) rate, for reference</span>
                <span>₹{costPreview.adjusted_daily_cost.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Present</span>
                <span>{costPreview.team_size_present} / {costPreview.team_size_expected}</span>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold py-2 rounded text-sm disabled:opacity-60"
          >
            {submitting ? 'Saving...' : 'Save Daily Entry'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DailyEntryPage;
